import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Clock, Plus } from "lucide-react";
import type { HistorySession } from "@/hooks/use-practice-sessions";
import { useBookmarkedSessions } from "@/hooks/use-session-bookmark";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useSidebar } from "@/components/ui/use-sidebar";
import {
  SidebarEmptyState,
  SidebarHeader,
  SidebarPrimaryAction,
  SidebarSearchField,
  SidebarSection,
  SidebarSkeleton,
} from "@/components/layout/SidebarShell";
import { SessionItemWithActions } from "./SessionItemWithActions";
import { BookmarkItemWithActions } from "./BookmarkItemWithActions";
import { GenerationStatus } from "./GenerationStatus";
import { useTranslation } from "react-i18next";

interface PracticeSidebarProps {
  history: HistorySession[];
  pinnedSessions: string[];
  activeTopicId?: string;
  onTogglePin: (sessionId: string) => void;
  onDeleteSession: (sessionId: string) => void;
  onSelectSession?: (session: HistorySession) => void;
  loading: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
}

export const PracticeSidebar = ({
  history,
  pinnedSessions,
  activeTopicId,
  onTogglePin,
  onDeleteSession,
  onSelectSession,
  loading,
  onLoadMore,
  hasMore,
}: PracticeSidebarProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  const [historyOpen, setHistoryOpen] = useState(true);
  const [pinnedOpen, setPinnedOpen] = useState(true);
  const [bookmarksOpen, setBookmarksOpen] = useState(true);
  const [search, setSearch] = useState("");
  // Every list in this rail filters on the settled term.
  const debouncedSearch = useDebouncedValue(search, 300);
  const { bookmarkedSessions } = useBookmarkedSessions();
  const [displayCount, setDisplayCount] = useState(15);

  const matches = (text: string) =>
    text.toLowerCase().includes(debouncedSearch.trim().toLowerCase());

  const pinnedHistory = history.filter(
    (session) => pinnedSessions.includes(session.id) && matches(session.topic)
  );
  const recentHistory = history
    .filter((session) => !pinnedSessions.includes(session.id) && matches(session.topic))
    .slice(0, displayCount);
  const filteredBookmarks = bookmarkedSessions
    .filter((session) => matches(session.topic))
    .slice(0, 10);

  const closeOnMobile = () => {
    if (isMobile) setOpenMobile(false);
  };

  const handleSessionClick = (session: HistorySession) => {
    if (onSelectSession) {
      onSelectSession(session);
    } else {
      navigate(`/session/${session.slug}`);
    }
    closeOnMobile();
  };

  const handleNewTopic = () => {
    navigate("/my-topics");
    closeOnMobile();
  };

  const showMore =
    !debouncedSearch.trim() &&
    (hasMore || history.length > displayCount + pinnedHistory.length);

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <SidebarHeader title={t("pages.myTopics.sidebar.topicsTitle", "Topics")} />

      <SidebarPrimaryAction icon={Plus} label={t("pages.myTopics.sidebar.newTopic", "New Topic")} onClick={handleNewTopic} />

      <SidebarSearchField
        value={search}
        onChange={setSearch}
        placeholder={t("pages.myTopics.sidebar.searchTopics", "Search topics...")}
      />

      <div className="px-2">
        <GenerationStatus isMobile={isMobile} />
      </div>

      {loading && history.length === 0 ? (
        <SidebarSkeleton />
      ) : (
        <div className="flex flex-col">
          {pinnedHistory.length > 0 && (
            <SidebarSection title={t("pages.myTopics.sidebar.pinned", "Pinned")} open={pinnedOpen} onOpenChange={setPinnedOpen}>
              {pinnedHistory.map((session) => (
                <SessionItemWithActions
                  key={session.id}
                  session={session}
                  isPinned={true}
                  isMobile={isMobile}
                  active={activeTopicId === session.id}
                  onTogglePin={onTogglePin}
                  onDelete={onDeleteSession}
                  onClick={() => handleSessionClick(session)}
                />
              ))}
            </SidebarSection>
          )}

          {filteredBookmarks.length > 0 && (
            <SidebarSection
              title={t("pages.myTopics.sidebar.bookmarked", "Bookmarked")}
              open={bookmarksOpen}
              onOpenChange={setBookmarksOpen}
            >
              {filteredBookmarks.map((session) => (
                <BookmarkItemWithActions
                  key={session.bookmark_id}
                  session={session}
                  isMobile={isMobile}
                  onRemove={() => {}}
                  onClick={() => {
                    navigate(`/session/${session.slug}`);
                    closeOnMobile();
                  }}
                />
              ))}
            </SidebarSection>
          )}

          <SidebarSection
            title={t("pages.myTopics.sidebar.history", "History")}
            open={historyOpen}
            onOpenChange={setHistoryOpen}
            data-tour="sidebar-history"
          >
            {recentHistory.length > 0 ? (
              recentHistory.map((session) => (
                <SessionItemWithActions
                  key={session.id}
                  session={session}
                  isPinned={pinnedSessions.includes(session.id)}
                  isMobile={isMobile}
                  active={activeTopicId === session.id}
                  onTogglePin={onTogglePin}
                  onDelete={onDeleteSession}
                  onClick={() => handleSessionClick(session)}
                />
              ))
            ) : (
              <SidebarEmptyState
                icon={Clock}
                message={debouncedSearch.trim()
                  ? t("pages.myTopics.sidebar.noMatchingTopics", "No matching topics")
                  : t("pages.myTopics.sidebar.noHistoryYet", "No history yet")}
              />
            )}

            {showMore && (
              <button
                onClick={() => {
                  if (hasMore) onLoadMore?.();
                  setDisplayCount((prev) => prev + 15);
                }}
                className="mx-2 mt-1 flex items-center justify-center gap-1 rounded-md py-2 text-sm font-medium text-primary transition-colors hover:bg-accent/60 hover:text-primary/80"
              >
                <span>More</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
          </SidebarSection>
        </div>
      )}
    </div>
  );
};
